import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eye, ImagePlus, Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "./api";
import { RichTextEditor } from "./RichTextEditor";
import { btnGhost, btnPrimary, CATEGORY_LABELS, ConfirmButton, inputCls, labelCls, Spinner } from "./ui";

const EMPTY = {
    title_el: "", title_en: "", slug: "", type: "article", category: "article", status: "draft",
    excerpt_el: "", excerpt_en: "", content_el: "", content_en: "",
    featured_image: "", instagram_url: "", published_at: "",
    seo_title_el: "", seo_title_en: "", seo_desc_el: "", seo_desc_en: "",
};

const GR_MAP = {
    α: "a", ά: "a", β: "v", γ: "g", δ: "d", ε: "e", έ: "e", ζ: "z", η: "i", ή: "i", θ: "th",
    ι: "i", ί: "i", ϊ: "i", ΐ: "i", κ: "k", λ: "l", μ: "m", ν: "n", ξ: "x", ο: "o", ό: "o",
    π: "p", ρ: "r", σ: "s", ς: "s", τ: "t", υ: "y", ύ: "y", ϋ: "y", ΰ: "y", φ: "f", χ: "ch", ψ: "ps", ω: "o", ώ: "o",
};

const slugify = (text) =>
    text.toLowerCase().split("").map((c) => GR_MAP[c] || c).join("")
        .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 120);

export const ArticleEditorPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = !id;
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [showEn, setShowEn] = useState(false);
    const [preview, setPreview] = useState(false);
    const slugTouched = useRef(false);
    const fileRef = useRef(null);

    useEffect(() => {
        if (isNew) return;
        api.get(`/api/admin/articles/${id}`)
            .then((r) => {
                const { id: _id, created_at, updated_at, ...data } = r.data;
                setForm({ ...EMPTY, ...data });
                slugTouched.current = true;
            })
            .catch((e) => {
                toast.error(apiError(e, "Το άρθρο δεν βρέθηκε."));
                navigate("/articles");
            })
            .finally(() => setLoading(false));
    }, [id, isNew, navigate]);

    const set = (key) => (e) => {
        const value = e?.target ? e.target.value : e;
        setForm((f) => {
            const next = { ...f, [key]: value };
            if (key === "title_el" && !slugTouched.current) next.slug = slugify(value);
            return next;
        });
    };

    const uploadImage = async (file) => {
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const res = await api.post("/api/admin/upload", fd);
            setForm((f) => ({ ...f, featured_image: res.data.url }));
            toast.success("Η εικόνα ανέβηκε.");
        } catch (e) {
            toast.error(apiError(e, "Το ανέβασμα απέτυχε."));
        } finally {
            setUploading(false);
        }
    };

    const save = async (statusOverride) => {
        const payload = { ...form, status: statusOverride || form.status };
        if (!payload.title_el.trim()) {
            toast.error("Ο τίτλος (Ελληνικά) είναι υποχρεωτικός.");
            return;
        }
        if (!payload.slug.trim()) {
            toast.error("Το slug είναι υποχρεωτικό.");
            return;
        }
        setSaving(true);
        try {
            const res = isNew
                ? await api.post("/api/admin/articles", payload)
                : await api.put(`/api/admin/articles/${id}`, payload);
            toast.success(payload.status === "published" ? "Το άρθρο δημοσιεύτηκε." : "Το άρθρο αποθηκεύτηκε ως πρόχειρο.");
            if (isNew) navigate(`/articles/${res.data.id}`, { replace: true });
            else setForm((f) => ({ ...f, status: res.data.status }));
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        try {
            await api.delete(`/api/admin/articles/${id}`);
            toast.success("Το άρθρο διαγράφηκε.");
            navigate("/articles");
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    if (loading) return <Spinner testId="editor-loading" />;

    return (
        <div className="mx-auto max-w-6xl pb-24" data-testid="article-editor-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <Link to="/articles" className="flex items-center gap-2 text-sm font-semibold text-[#4CC9DC] hover:text-[#F7EAB8]" data-testid="editor-back">
                    <ArrowLeft size={16} /> Όλα τα άρθρα
                </Link>
                <div className="flex flex-wrap items-center gap-2">
                    <button onClick={() => setPreview(true)} className={btnGhost} data-testid="article-preview-button">
                        <Eye size={14} /> Προεπισκόπηση
                    </button>
                    {!isNew && <ConfirmButton onConfirm={remove} label="Διαγραφή" testId="article-delete-button" />}
                    <button onClick={() => save("draft")} disabled={saving} className={btnGhost} data-testid="article-save-draft-button">
                        Αποθήκευση πρόχειρου
                    </button>
                    <button onClick={() => save("published")} disabled={saving} className={btnPrimary} data-testid="article-save-button">
                        {saving && <Loader2 size={14} className="animate-spin" />}
                        {form.status === "published" ? "Αποθήκευση & Δημοσίευση" : "Δημοσίευση"}
                    </button>
                </div>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_320px]">
                {/* Main column */}
                <div className="space-y-5">
                    <div className="rounded-2xl border border-white/10 bg-[#093448] p-5">
                        <span className={labelCls}>Τύπος</span>
                        <div className="flex gap-2" data-testid="article-type-toggle">
                            {[["article", "Άρθρο"], ["instagram", "Instagram"]].map(([val, label]) => (
                                <button
                                    key={val}
                                    type="button"
                                    onClick={() => set("type")(val)}
                                    className={`rounded-full border px-4 py-1.5 text-xs font-bold transition-colors ${
                                        form.type === val ? "border-[#22A6C0] bg-[#22A6C0]/15 text-[#4CC9DC]" : "border-white/10 text-[#A3B3B8]"
                                    }`}
                                    data-testid={`article-type-${val}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-4 rounded-2xl border border-white/10 bg-[#093448] p-5">
                        <div>
                            <label className={labelCls} htmlFor="f-title">Τίτλος (Ελληνικά) *</label>
                            <input id="f-title" value={form.title_el} onChange={set("title_el")} className={inputCls} data-testid="article-title-el" />
                        </div>
                        <div>
                            <label className={labelCls} htmlFor="f-slug">Slug *</label>
                            <input
                                id="f-slug"
                                value={form.slug}
                                onChange={(e) => { slugTouched.current = true; set("slug")(e); }}
                                className={inputCls}
                                data-testid="article-slug"
                            />
                            <p className="mt-1 text-[11px] text-[#697B80]">/nea/{form.slug || "…"}</p>
                        </div>
                        <div>
                            <label className={labelCls} htmlFor="f-excerpt">Απόσπασμα / Σύντομη λεζάντα</label>
                            <textarea id="f-excerpt" rows={3} value={form.excerpt_el} onChange={set("excerpt_el")} className={inputCls} data-testid="article-excerpt-el" />
                        </div>
                        {form.type === "instagram" ? (
                            <div>
                                <label className={labelCls} htmlFor="f-ig">Σύνδεσμος δημοσίευσης (Instagram ή Facebook)</label>
                                <input id="f-ig" type="url" placeholder="https://www.instagram.com/p/… ή https://www.facebook.com/…" value={form.instagram_url} onChange={set("instagram_url")} className={inputCls} data-testid="article-instagram-url" />
                                <p className="mt-1 text-[11px] text-[#697B80]">Επικόλλησε τον σύνδεσμο του post — στη δημόσια σελίδα η δημοσίευση θα εμφανίζεται ενσωματωμένη με τη φωτογραφία της. Αν ο σύνδεσμος δεν είναι απευθείας post, εμφανίζεται κάρτα προεπισκόπησης με σύνδεσμο.</p>
                            </div>
                        ) : (
                            <div>
                                <span className={labelCls}>Περιεχόμενο (Ελληνικά)</span>
                                <RichTextEditor value={form.content_el} onChange={set("content_el")} testId="article-content-el" />
                            </div>
                        )}
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-[#093448] p-5">
                        <button type="button" onClick={() => setShowEn(!showEn)} className="flex w-full items-center justify-between text-sm font-bold text-[#A3B3B8]" data-testid="article-en-toggle">
                            English version (προαιρετικό — αλλιώς εμφανίζονται τα Ελληνικά)
                            <span className="text-[#4CC9DC]">{showEn ? "−" : "+"}</span>
                        </button>
                        {showEn && (
                            <div className="mt-4 space-y-4" data-testid="article-en-fields">
                                <div>
                                    <label className={labelCls}>Title (EN)</label>
                                    <input value={form.title_en} onChange={set("title_en")} className={inputCls} data-testid="article-title-en" />
                                </div>
                                <div>
                                    <label className={labelCls}>Excerpt (EN)</label>
                                    <textarea rows={2} value={form.excerpt_en} onChange={set("excerpt_en")} className={inputCls} data-testid="article-excerpt-en" />
                                </div>
                                {form.type === "article" && (
                                    <div>
                                        <span className={labelCls}>Content (EN)</span>
                                        <RichTextEditor value={form.content_en} onChange={set("content_en")} testId="article-content-en" />
                                    </div>
                                )}
                                <div>
                                    <label className={labelCls}>SEO title (EN)</label>
                                    <input value={form.seo_title_en} onChange={set("seo_title_en")} className={inputCls} data-testid="article-seo-title-en" />
                                </div>
                                <div>
                                    <label className={labelCls}>Meta description (EN)</label>
                                    <textarea rows={2} value={form.seo_desc_en} onChange={set("seo_desc_en")} className={inputCls} data-testid="article-seo-desc-en" />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sidebar column */}
                <div className="space-y-5">
                    <div className="space-y-4 rounded-2xl border border-white/10 bg-[#093448] p-5">
                        <div>
                            <label className={labelCls}>Κατάσταση</label>
                            <select value={form.status} onChange={set("status")} className={inputCls} data-testid="article-status">
                                <option value="draft">Πρόχειρο</option>
                                <option value="published">Δημοσιευμένο</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelCls}>Κατηγορία</label>
                            <select value={form.category} onChange={set("category")} className={inputCls} data-testid="article-category">
                                {Object.entries(CATEGORY_LABELS).map(([val, label]) => (
                                    <option key={val} value={val}>{label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={labelCls}>Ημερομηνία δημοσίευσης</label>
                            <input type="date" value={form.published_at || ""} onChange={set("published_at")} className={`${inputCls} [color-scheme:dark]`} data-testid="article-published-at" />
                        </div>
                    </div>

                    <div className="space-y-3 rounded-2xl border border-white/10 bg-[#093448] p-5">
                        <span className={labelCls}>Επιλεγμένη εικόνα</span>
                        {form.featured_image ? (
                            <div className="relative overflow-hidden rounded-xl border border-white/10" data-testid="article-image-preview">
                                <img src={form.featured_image} alt="" className="aspect-video w-full object-cover" />
                                <button
                                    type="button"
                                    onClick={() => setForm((f) => ({ ...f, featured_image: "" }))}
                                    className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-red-500/80"
                                    aria-label="Αφαίρεση εικόνας"
                                    data-testid="article-image-remove"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                disabled={uploading}
                                className="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 text-[#697B80] transition-colors hover:border-[#4CC9DC] hover:text-[#4CC9DC]"
                                data-testid="article-image-upload-button"
                            >
                                {uploading ? <Loader2 size={22} className="animate-spin" /> : <ImagePlus size={22} />}
                                <span className="text-xs font-semibold">{uploading ? "Ανέβασμα…" : "Ανέβασμα εικόνας"}</span>
                            </button>
                        )}
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            className="hidden"
                            onChange={(e) => uploadImage(e.target.files?.[0])}
                            data-testid="article-image-input"
                        />
                    </div>

                    <div className="space-y-4 rounded-2xl border border-white/10 bg-[#093448] p-5">
                        <span className={labelCls}>SEO (Ελληνικά)</span>
                        <div>
                            <label className={labelCls}>SEO τίτλος</label>
                            <input value={form.seo_title_el} onChange={set("seo_title_el")} className={inputCls} data-testid="article-seo-title-el" />
                        </div>
                        <div>
                            <label className={labelCls}>Meta περιγραφή</label>
                            <textarea rows={3} value={form.seo_desc_el} onChange={set("seo_desc_el")} className={inputCls} data-testid="article-seo-desc-el" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Preview modal */}
            {preview && (
                <div className="fixed inset-0 z-50 overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" data-testid="article-preview-modal">
                    <div className="mx-auto my-8 max-w-3xl rounded-3xl border border-white/10 bg-[#052536] p-6 md:p-10">
                        <div className="mb-6 flex items-center justify-between">
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#697B80]">Προεπισκόπηση</p>
                            <button onClick={() => setPreview(false)} className="rounded-full border border-white/10 p-2 text-[#A3B3B8] hover:text-white" aria-label="Κλείσιμο" data-testid="article-preview-close">
                                <X size={16} />
                            </button>
                        </div>
                        <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#F7EAB8]">{CATEGORY_LABELS[form.category]}</p>
                        <h1 className="font-display mt-3 text-3xl leading-tight text-[#F2F5F6] md:text-4xl">{form.title_el || "Χωρίς τίτλο"}</h1>
                        {form.featured_image && (
                            <img src={form.featured_image} alt="" className="mt-6 w-full rounded-2xl border border-white/10 object-cover" />
                        )}
                        {form.type === "instagram" ? (
                            <p className="mt-6 text-[#A3B3B8]">{form.excerpt_el}</p>
                        ) : (
                            <div className="article-content mt-8 rounded-2xl bg-white p-6" dangerouslySetInnerHTML={{ __html: form.content_el }} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
